import { useQuery } from '@apollo/client';
import { AddCircle, VerifiedUser } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { PageContainer } from '~/components/page-layout';
import { SearchContainer } from '~/components/search';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useTabs } from '../lib/tabs';
import { RoleUserListDocument } from './RoleUserList.generated';
import { AddUsersDialog } from './components/AddUsersDialog';
import { BulkActionsMenu } from './components/BulkActionsMenu';
import { UserTable } from './components/UserTable';

export const RoleUserList = () => {
  const { t } = useTranslation();
  const params = useParams<{ roleId: string }>();
  const roleId = parseInt(params.roleId ?? '');

  const { currentNetwork } = useAppContext();
  const tabs = useTabs();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { data, loading, error } = useQuery(RoleUserListDocument, {
    variables: { networkId: currentNetwork.id, roleId, search },
  });

  const [addUsersOpen, setAddUsersOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  // Update selection if data changes...this seems overly complicated but works
  useEffect(() => {
    if (!data?.network?.role.users) return;
    const userIds = new Set<GridRowId>(data.network.role.users.map((user) => user.id));
    if (selectedIds.every((id) => userIds.has(id))) return;
    setSelectedIds(selectedIds.filter((id) => userIds.has(id)));
  }, [data?.network?.role.users, selectedIds]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  const addUsersButton = (
    <Button
      color="primary"
      onClick={() => setAddUsersOpen(true)}
      startIcon={<AddCircle />}
      variant="contained"
    >
      {t('pages.roles.buttons.add_users')}
    </Button>
  );

  return (
    <>
      {data?.network && (
        <>
          <Toolbar
            actions={addUsersButton}
            breadcrumbsLabel={<RouterBreadcrumbs />}
            titleIcon={<VerifiedUser />}
            titleText={data.network.role.name}
          />

          <ScrollableRouterTabs current={t('pages.roles.tabs.users')} tabs={tabs} />

          <PageContainer>
            <SearchContainer>
              <SearchBar placeholder={t('pages.roles.users.search_placeholder')} search={search} />
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 4 }}>
                <BulkActionsMenu role={data.network.role} selectedIds={selectedIds} />
                <Count
                  selectedCount={selectedIds.length}
                  totalCount={data.network.role.users.length}
                  thing="pages.roles.users.thing_plural"
                />
              </Box>
            </SearchContainer>
            <UserTable
              checkboxSelection={data.network.role.users.length > 1}
              disableRowSelectionOnClick={data.network.role.users.length < 2}
              loading={loading}
              onRowSelectionModelChange={setSelectedIds}
              role={data.network.role}
              rows={data.network.role.users}
              rowSelectionModel={selectedIds}
            />
          </PageContainer>

          {addUsersOpen && (
            <AddUsersDialog
              fullWidth
              onClose={() => setAddUsersOpen(false)}
              open
              role={data.network.role}
              users={data.network.userIds.nodes}
            />
          )}
        </>
      )}
    </>
  );
};
